import { statusDePantalla } from '@/lib/operations/queues';
import { desdeCuandoMira, type OpsKpiPeriod } from '@/lib/operations/ordersApi';

/**
 * LA declaración de las colas de Operaciones. Una sola, y todo lo demás se deriva.
 *
 * 🚨 POR QUÉ EXISTE. Las mismas cinco colas estaban enumeradas a mano en cinco archivos, y la regla
 *    «cuál abre mirando todo el histórico» en tres. En un solo día de trabajo esa repetición produjo
 *    TRES defectos reales, y los tres los encontró el founder mirando la pantalla:
 *
 *      «Ventas carga más rápido que Canceladas»          → la copia de la precarga
 *      «sólo las canceladas sale un pequeño loading»     → la copia de los KPIs
 *      «Por confirmar sigue con el loading»              → la lista del menú, con una sola cola
 *
 *    Ninguno dio error, ni log, ni prueba en rojo. Una precarga desincronizada NO SE VE ROTA: se ve
 *    lenta, que es el defecto más caro de encontrar.
 *
 * ⭐ LA LECCIÓN NO FUE «TENER MÁS CUIDADO». Tres copias en un día significan que el dato vivía en un
 *    lugar incómodo de alcanzar: la pantalla tiene 1.300 líneas y la precarga no puede importarla,
 *    así que cada archivo reescribía la regla porque le quedaba más a mano que ir a buscarla. Este
 *    módulo es liviano a propósito —sólo datos y dos funciones puras— para que alcanzarlo sea
 *    siempre más fácil que copiarlo.
 *
 * 🪤 AGREGAR UNA COLA ES AGREGAR UNA FILA ACÁ. Si falta un campo no compila, y `rutaDeCola` no puede
 *    construir su ruta sin él. Eso es lo que hace que no haga falta acordarse de nada.
 */

export type ColaDeOperaciones = 'ventas' | 'pedidos' | 'cobranzas' | 'logistica' | 'canceladas' | 'drop';

interface DeclaracionDeCola {
  /** El módulo de su pantalla. Función para que sea `import()` diferido, no estático. */
  pagina: () => Promise<{ default: React.ComponentType }>;
  /**
   * ¿Es una cola de TRABAJO o una pantalla de MEDICIÓN?
   *
   * Una cola de trabajo muestra un STOCK: lo pendiente es lo pendiente, sin importar de cuándo — y
   * lo más viejo es lo que más importa. Por eso abre mirando todo el histórico. Una pantalla de
   * medición muestra un FLUJO, que sí vive dentro de un lapso.
   *
   * 🪤 Canceladas y Ventas NO son colas de trabajo: nadie «resuelve» una venta hecha.
   */
  deTrabajo: boolean;
  /** Sólo Cobranzas abre acotada a lo que tiene saldo. */
  soloConSaldo?: boolean;
  /** El canal base cuando el usuario no eligió ninguno. */
  canalBase?: string[];
}

export const COLAS: Record<ColaDeOperaciones, DeclaracionDeCola> = {
  ventas: {
    pagina: () => import('@/pages/operaciones/VentasPage'),
    deTrabajo: false,
  },
  pedidos: {
    pagina: () => import('@/pages/operaciones/PedidosPage'),
    deTrabajo: true,
  },
  cobranzas: {
    pagina: () => import('@/pages/operaciones/OpsCobranzasPage'),
    deTrabajo: true,
    soloConSaldo: true,
  },
  logistica: {
    pagina: () => import('@/pages/operaciones/OpsLogisticaPage'),
    deTrabajo: true,
  },
  canceladas: {
    pagina: () => import('@/pages/operaciones/CanceladasPage'),
    deTrabajo: false,
  },
  /*
   * 🚨 DROP ERA LA UNICA COLA SIN PRECARGA, y no por una decision: simplemente nadie la agrego
   *    cuando se le pusieron loaders a las otras cuatro. Es exactamente el olvido que esta
   *    declaracion existe para hacer imposible.
   *
   * 🪤 Y SI ES COLA DE TRABAJO: un pedido del almacen sin despachar importa igual tenga tres dias o
   *    tres semanas — de hecho importa MAS cuanto mas viejo. Abre mirando todo el historico.
   */
  drop: {
    pagina: () => import('@/pages/operaciones/DropPage'),
    deTrabajo: true,
    canalBase: ['smartydrop'],
  },
};

/** Todas las colas, derivadas. Recorrer esto es cómo una cola nueva entra sola en todos lados. */
export const TODAS_LAS_COLAS = Object.keys(COLAS) as ColaDeOperaciones[];

/** El período con el que abre cada pantalla. La leen la precarga, la lista y los KPIs. */
export function periodoInicialDeCola(cola: string): OpsKpiPeriod {
  return COLAS[cola as ColaDeOperaciones]?.deTrabajo ? 'todo' : '7d';
}

export interface VistaInicialDeCola {
  stages: string[];
  search: string;
  paymentStatus: string[];
  shippingStatus: string[];
  channel: string[];
  billingStatus: string[];
  registraLaGuia: string[];
  modalidad: string[];
  onlyWithDue: boolean | undefined;
  createdFrom: string | undefined;
  sort: string;
  offset: number;
  pageSize: number;
}

/**
 * Con qué abre una cola. La precarga de la ruta y la pantalla leen ESTO, no cada una lo suyo.
 *
 * 🪤 `stages` se DERIVA de `statusDePantalla`, la misma función que usa la pantalla. Copiar la lista
 *    de etapas fue el primer defecto de esta serie: la precarga pedía el vocabulario viejo
 *    (`['sale','fulfilled','closed']`) mientras la pantalla pedía `['open','closed']`, y calentaba
 *    una casilla que nadie leía desde que ADR-0029 cambió el modelo.
 */
export function vistaInicialDeCola(cola: string): VistaInicialDeCola {
  const d = COLAS[cola as ColaDeOperaciones];
  return {
    stages: statusDePantalla(cola === 'drop' ? 'ventas' : cola),
    search: '',
    paymentStatus: [],
    shippingStatus: [],
    channel: d?.canalBase ?? [],
    billingStatus: [],
    registraLaGuia: [],
    modalidad: [],
    onlyWithDue: d?.soloConSaldo ? true : undefined,
    createdFrom: desdeCuandoMira(periodoInicialDeCola(cola)) ?? undefined,
    /*
     * 🚨 POR LA FECHA EFECTIVA, LA MISMA QUE SE MUESTRA. El founder lo pescó con una captura: la
     *    venta de Mercedes ya decía «28 ago» y seguía ubicada entre las del 6 de agosto — mostrando
     *    una fecha y ordenando por otra, que es peor que mostrar la vieja: la tabla queda en un
     *    orden que no se puede explicar mirándola.
     *
     * 🪤 YO HABÍA ARREGLADO SÓLO EL VALOR POR DEFECTO de `fetchOrdersPage`, y esta línea lo pisa.
     *    Un default no protege de nadie que declare el suyo — y la lección es la misma que ya había
     *    dejado escrita el propio archivo: cuando un dato vive en varios lugares, arreglar uno da la
     *    sensación de haber terminado.
     */
    sort: 'fecha_efectiva desc',
    offset: 0,
    pageSize: 25,
  };
}
